import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Package, Trash2, Pencil, Search, MapPin, History, Receipt, Minus, AlertTriangle, ArrowUpDown } from "lucide-react";
import { api, money } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, EmptyState } from "@/components/States";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const EMPTY = { name: "", sku: "", cost_price: "", unit_price: "", stock_qty: "", reorder_level: "", location: "" };

const isLow = (p) => p.reorder_level != null && p.reorder_level !== "" && (p.stock_qty ?? 0) <= p.reorder_level;

export default function Parts() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [sort, setSort] = useState("name");
  const [lowOnly, setLowOnly] = useState(false);
  const [adjusting, setAdjusting] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setParts(await api("/parts")); setGate(null); }
    catch (e) { if (e.status === 402) setGate(e.body?.detail || {}); else toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    const value = parts.reduce((s, p) => s + (p.stock_qty ?? 0) * (p.cost_price ?? p.unit_price ?? 0), 0);
    return { count: parts.length, value, low: parts.filter(isLow).length };
  }, [parts]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let list = parts.filter((p) => !t || [p.name, p.sku, p.location].filter(Boolean).some((f) => f.toLowerCase().includes(t)));
    if (lowOnly) list = list.filter(isLow);
    const by = {
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
      stock: (a, b) => (a.stock_qty ?? 0) - (b.stock_qty ?? 0),
      value: (a, b) => (b.stock_qty ?? 0) * (b.cost_price ?? b.unit_price ?? 0) - (a.stock_qty ?? 0) * (a.cost_price ?? a.unit_price ?? 0),
    };
    return [...list].sort(by[sort] || by.name);
  }, [parts, q, lowOnly, sort]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ name: p.name || "", sku: p.sku || "", cost_price: p.cost_price ?? "", unit_price: p.unit_price ?? "", stock_qty: p.stock_qty ?? "", reorder_level: p.reorder_level ?? "", location: p.location || "" }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    const body = { name: form.name.trim(), sku: form.sku || null, cost_price: form.cost_price === "" ? null : Number(form.cost_price), unit_price: Number(form.unit_price) || 0, stock_qty: Number(form.stock_qty) || 0, reorder_level: form.reorder_level === "" ? null : Number(form.reorder_level), location: form.location.trim() || null };
    try {
      if (editId) { await api(`/parts/${editId}`, { method: "PATCH", body: JSON.stringify(body) }); toast.success("Part updated"); }
      else { await api("/parts", { method: "POST", body: JSON.stringify(body) }); toast.success("Part added"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Delete part?")) return; try { await api(`/parts/${id}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };

  const adjust = async (p, delta) => {
    setAdjusting(p.id);
    // optimistic
    setParts((prev) => prev.map((x) => (x.id === p.id ? { ...x, stock_qty: Math.max((x.stock_qty ?? 0) + delta, 0) } : x)));
    try { await api(`/parts/${p.id}/adjust`, { method: "POST", body: JSON.stringify({ delta }) }); }
    catch (e) { toast.error(e.message); load(); } finally { setAdjusting(null); }
  };

  if (loading) return <Loading />;
  if (gate) return <><PageHeader title="Parts & Inventory" subtitle="Stock management" /><UpgradePrompt title="Parts & Inventory is a Pro feature" message={gate.message} minTier={gate.min_tier || "pro"} testid="parts-upgrade" /></>;
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Parts" subtitle="Inventory, pricing & storage location"
        actions={<div className="flex gap-2">
          <Link to="/money?tab=expenses" data-testid="parts-expenses-link" className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-bold text-brand-2 hover:bg-secondary"><Receipt className="h-4 w-4" />Expenses</Link>
          <button onClick={openNew} data-testid="add-part-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" />Add Part</button>
        </div>} />

      {/* Summary bar */}
      <div className="mb-4 grid grid-cols-3 gap-3" data-testid="parts-summary">
        <button onClick={() => { setLowOnly(false); setQ(""); }} data-testid="summary-count-btn" className={`rounded-xl border p-3 text-center transition-colors ${!lowOnly && !q ? "border-brand bg-brand-tertiary" : "border-border bg-card hover:bg-secondary"}`}>
          <p className="font-display text-xl font-bold text-midnight" data-testid="summary-count">{summary.count}</p>
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Parts</p>
        </button>
        <div className="rounded-xl border border-border bg-card p-3 text-center">
          <p className="font-display text-xl font-bold text-midnight" data-testid="summary-value">{money(summary.value)}</p>
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Stock value</p>
        </div>
        <button onClick={() => setLowOnly((v) => !v)} data-testid="summary-low" className={`rounded-xl border p-3 text-center transition-colors ${lowOnly ? "border-warning bg-warning-bg" : "border-border bg-card"}`}>
          <p className={`font-display text-xl font-bold ${summary.low > 0 ? "text-warning" : "text-midnight"}`}>{summary.low}</p>
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Low stock</p>
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU or location…" data-testid="part-search"
            className="w-full rounded-lg border border-input bg-card py-2.5 pl-10 pr-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
        </div>
        <div className="relative">
          <ArrowUpDown className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="part-sort" className="appearance-none rounded-lg border border-input bg-card py-2.5 pl-9 pr-8 text-sm font-semibold outline-none focus:border-brand">
            <option value="name">Name (A–Z)</option>
            <option value="stock">Stock (low→high)</option>
            <option value="value">Stock value (high→low)</option>
          </select>
        </div>
        {lowOnly && <button onClick={() => setLowOnly(false)} data-testid="clear-low-filter" className="rounded-lg border border-warning bg-warning-bg px-3 py-2.5 text-xs font-bold text-warning">Low stock only ✕</button>}
      </div>

      {filtered.length === 0 ? <EmptyState icon={Package} title={q || lowOnly ? "No matching parts" : "No parts yet"} /> : (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {filtered.map((p) => {
            const low = isLow(p);
            const margin = p.cost_price != null ? p.unit_price - p.cost_price : null;
            const marginPct = p.cost_price != null && p.unit_price > 0 ? Math.round((margin / p.unit_price) * 100) : null;
            return (
              <div key={p.id} data-testid={`part-${p.id}`} className={`flex items-center gap-3 px-4 py-3.5 ${low ? "bg-warning-bg/40" : ""}`}>
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 truncate font-bold text-midnight">
                    {low && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" data-testid={`part-low-${p.id}`} />}{p.name}
                  </p>
                  <p className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    {p.sku && <span>SKU {p.sku}</span>}
                    {p.cost_price != null && <span>Cost {money(p.cost_price)}</span>}
                    {margin != null && <span className={margin >= 0 ? "text-success" : "text-danger"} data-testid={`part-margin-${p.id}`}>Margin {money(margin)}{marginPct != null ? ` · ${marginPct}%` : ""}</span>}
                    {p.location && <span className="inline-flex items-center gap-1 text-brand-2"><MapPin className="h-3 w-3" />{p.location}</span>}
                  </p>
                </div>
                {/* Quick stock stepper */}
                <div className="flex items-center gap-1.5" data-testid={`part-stepper-${p.id}`}>
                  <button onClick={() => adjust(p, -1)} disabled={adjusting === p.id || (p.stock_qty ?? 0) <= 0} data-testid={`part-dec-${p.id}`} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-midnight hover:bg-secondary disabled:opacity-40"><Minus className="h-3.5 w-3.5" /></button>
                  <span className={`min-w-[2.5rem] text-center font-display text-sm font-bold ${low ? "text-warning" : "text-midnight"}`} data-testid={`part-stock-${p.id}`}>{p.stock_qty ?? 0}</span>
                  <button onClick={() => adjust(p, 1)} disabled={adjusting === p.id} data-testid={`part-inc-${p.id}`} className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-midnight hover:bg-secondary disabled:opacity-40"><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <p className="w-16 text-right font-display text-lg font-bold text-midnight">{money(p.unit_price)}</p>
                <button onClick={() => setDetail(p.id)} data-testid={`part-usage-${p.id}`} className="text-brand-2"><History className="h-4 w-4" /></button>
                <button onClick={() => openEdit(p)} data-testid={`edit-part-${p.id}`} className="text-brand-2"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(p.id)} data-testid={`delete-part-${p.id}`} className="text-danger"><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit Part" : "Add Part"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" data-testid="part-name" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="SKU (optional)" data-testid="part-sku" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location (e.g. Shelf B3, Bin 12)" data-testid="part-location" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            <div className="flex gap-2">
              <input type="number" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="Cost £ (purchase)" data-testid="part-cost" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
              <input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="Sell £ (ex-VAT)" data-testid="part-price" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            <div className="flex gap-2">
              <input type="number" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} placeholder="Stock on hand" data-testid="part-stock" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
              <input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} placeholder="Reorder at (low-stock alert)" data-testid="part-reorder" className="w-full rounded-lg border border-input px-3.5 py-2.5 text-sm outline-none focus:border-brand" />
            </div>
            {form.cost_price !== "" && form.unit_price !== "" && (
              <p className="text-xs font-semibold text-success" data-testid="part-margin-preview">Margin per unit: {money(Number(form.unit_price) - Number(form.cost_price))}{Number(form.unit_price) > 0 ? ` (${Math.round(((Number(form.unit_price) - Number(form.cost_price)) / Number(form.unit_price)) * 100)}%)` : ""}</p>
            )}
          </div>
          <DialogFooter><button onClick={submit} disabled={saving} data-testid="part-save" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3 text-sm font-extrabold text-white disabled:opacity-60">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : editId ? "SAVE CHANGES" : "ADD PART"}</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <PartDetailDialog partId={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

const moveLabel = (reason, qty) => reason === "purchase_receipt" ? "received" : reason === "manual_adjust" ? "adjusted" : qty > 0 ? "issued" : "returned";

function PartDetailDialog({ partId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!partId) { setData(null); return; }
    api(`/parts/${partId}`).then(setData).catch(() => setData(null));
  }, [partId]);
  return (
    <Dialog open={!!partId} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display uppercase">{data?.name || "Part"}</DialogTitle></DialogHeader>
        {!data ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div> : (
          <div className="space-y-3 text-sm" data-testid="part-detail">
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span className="text-muted-foreground">Stock: <b className="text-midnight">{data.stock_qty ?? 0}</b></span>
              {data.reorder_level != null && <span className="text-muted-foreground">Reorder at: <b className="text-midnight">{data.reorder_level}</b></span>}
              {data.cost_price != null && <span className="text-muted-foreground">Cost: <b className="text-midnight">{money(data.cost_price)}</b></span>}
              <span className="text-muted-foreground">Sells: <b className="text-midnight">{money(data.unit_price)}</b></span>
              {data.location && <span className="text-muted-foreground">Location: <b className="text-midnight">{data.location}</b></span>}
            </div>
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Where used</p>
              {(data.usage || []).length === 0 ? <p className="text-xs text-muted-foreground">Not used on any job yet.</p> : (
                <div className="divide-y divide-border rounded-lg border border-border">
                  {data.usage.map((u, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2" data-testid={`part-usage-row-${i}`}>
                      <div className="min-w-0 flex-1">
                        {u.job_id ? <Link to={`/jobs/${u.job_id}`} onClick={onClose} className="truncate text-sm font-semibold text-brand-2">{u.job_title || "Job"}</Link> : <span className="text-sm">{u.reason === "manual_adjust" ? "Manual adjustment" : u.reason}</span>}
                        <p className="text-[11px] text-muted-foreground">{fmtDate(u.date)} · {moveLabel(u.reason, u.qty)}</p>
                      </div>
                      <span className={`font-display font-bold ${u.qty > 0 ? "text-success" : "text-midnight"}`}>{u.qty > 0 ? "+" : "−"}{Math.abs(u.qty)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
